import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { useAuth } from "../context/AuthContext";
import { GoogleButton } from "../components/GoogleButton";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    password: "",
    role: "seller",
  });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 sm:px-6 py-8 sm:py-16" data-testid="register-page">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-3xl text-primary tracking-tight">Create your account</CardTitle>
          <p className="text-sm text-muted-foreground">
            Join India's renewable energy marketplace. Your name, email and phone are shown to members on listings you post.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <GoogleButton label="Sign up with Google" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input data-testid="reg-name" value={form.name} onChange={upd("name")} required />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input data-testid="reg-company" value={form.company} onChange={upd("company")} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" data-testid="reg-email" value={form.email} onChange={upd("email")} required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input data-testid="reg-phone" value={form.phone} onChange={upd("phone")} placeholder="+91 ..." />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  data-testid="reg-password"
                  value={form.password}
                  onChange={upd("password")}
                  minLength={6}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>I am a…</Label>
              <RadioGroup
                value={form.role}
                onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                <label className="border rounded-xl p-3 cursor-pointer hover:border-primary flex items-start gap-3">
                  <RadioGroupItem value="seller" data-testid="reg-role-seller" />
                  <div>
                    <div className="font-medium">Seller</div>
                    <div className="text-xs text-muted-foreground">List projects, land, grid or power.</div>
                  </div>
                </label>
                <label className="border rounded-xl p-3 cursor-pointer hover:border-primary flex items-start gap-3">
                  <RadioGroupItem value="buyer" data-testid="reg-role-buyer" />
                  <div>
                    <div className="font-medium">Buyer</div>
                    <div className="text-xs text-muted-foreground">Discover and inquire about opportunities.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="reg-submit"
              disabled={loading}
            >
              {loading ? "Creating…" : "Create account"}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Already registered?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Log in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
